import { useCallback } from 'react';
import { EntityType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { isSmsMessageItem, isCallRecordItem } from '../../utils/thread-items-utils';
import { useDismissTagMutation } from '../mutations';
import { useUnifiedThreadUpdater } from '../query-updaters';

const DEFAULT_DELAY = 3500;

/**
 * Hook for dismissing a tag with undo functionality for unified threads.
 *
 * Flow:
 * 1. Immediately removes tag optimistically (for instant UI feedback)
 * 2. Calls onUndoAvailable with an undo callback (typically to show "Tag dismissed. [Undo]" notification)
 * 3. After delay + 1000ms, actually submits the dismiss mutation (if not cancelled)
 * 4. If user clicks undo, the undo callback re-adds the tag and cancels the mutation
 */
type UseDismissTagWithUndoArgs = {
  /** Called when undo becomes available. Typically used to show a notification with an undo button. */
  onUndoAvailable: (undoCallback: () => void, autoDismissAfter: number) => void;
  /** How long to wait before showing the undo option (default: 3500ms) */
  delay?: number;
  mutationOptions?: Parameters<typeof useDismissTagMutation>[0];
  onDismissError?: () => void;
};

export const useDismissTagWithUndo = ({
  onUndoAvailable,
  onDismissError,
  delay = DEFAULT_DELAY,
  mutationOptions,
}: UseDismissTagWithUndoArgs) => {
  // Disable optimistic update in the mutation since we handle it ourselves
  const dismissTagMutation = useDismissTagMutation({
    ...mutationOptions,
    optimisticUpdate: false,
  });
  const unifiedThreadUpdater = useUnifiedThreadUpdater();

  return useCallback(
    async (
      {
        originalAppliedBy,
        smsCreatedAt,
        ...request
      }: Parameters<typeof dismissTagMutation.mutate>[0] & { originalAppliedBy: string; smsCreatedAt: string },
      options?: Parameters<typeof dismissTagMutation.mutate>[1]
    ) => {
      // Store revert function for unified thread updates
      // eslint-disable-next-line prefer-const
      let revertUnifiedThreadUpdate: (() => void) | undefined;

      const timeout = setTimeout(() => {
        // Pass the revert functions to the mutation so onError can use them
        dismissTagMutation.mutate(request, {
          ...options,
          onError: (error, variables, context) => {
            // Revert the optimistic update done by useDismissTagWithUndo
            if (revertUnifiedThreadUpdate) {
              revertUnifiedThreadUpdate();
            }

            // Call the original onError if provided
            options?.onError?.(error, variables, context);
            onDismissError?.();
          },
        });
      }, delay + 1000);

      // Use unified thread updater for optimistic tag removal
      const requestData = request;
      const upsertResult = await unifiedThreadUpdater.upsertThreadItem({
        matchValues: {
          threadItemId: requestData.id,
          threadId: requestData.threadId,
          locationId: requestData.locationId,
        },
        updater: (threadItem) => {
          if (!threadItem) return threadItem;

          if (isSmsMessageItem(threadItem) && threadItem.smsMessage) {
            return {
              ...threadItem,
              smsMessage: {
                ...threadItem.smsMessage,
                tags: threadItem.smsMessage.tags.filter((id) => id !== requestData.tagId),
                tagsDetailed: threadItem.smsMessage.tagsDetailed?.filter((tag) => tag.tagId !== requestData.tagId),
              },
            };
          }
          if (isCallRecordItem(threadItem) && threadItem.callRecord) {
            return {
              ...threadItem,
              callRecord: {
                ...threadItem.callRecord,
                tags: threadItem.callRecord.tags?.filter((tag) => tag.tagId !== requestData.tagId) || [],
              },
            };
          }
          return threadItem;
        },
        isOptimistic: true,
        getThreadOnly: true,
      });

      // Store the revert function
      revertUnifiedThreadUpdate = upsertResult.revertCacheUpdate;

      // Call onUndoAvailable with the undo callback - this typically shows a notification with an "Undo" button
      onUndoAvailable(() => {
        clearTimeout(timeout);

        // Use unified thread updater to restore the tag
        unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadItemId: requestData.id,
            threadId: requestData.threadId,
            locationId: requestData.locationId,
          },
          updater: (threadItem) => {
            if (!threadItem) return threadItem;

            if (isSmsMessageItem(threadItem) && threadItem.smsMessage) {
              const existingTagIds = threadItem.smsMessage.tags || [];
              const tagExists = existingTagIds.includes(requestData.tagId);
              const newTagIds = tagExists ? existingTagIds : [...existingTagIds, requestData.tagId];

              const existingTagsDetailed = threadItem.smsMessage.tagsDetailed || [];
              const tagDetailedExists = existingTagsDetailed.some((tag) => tag.tagId === requestData.tagId);
              const newTagsDetailed = tagDetailedExists
                ? existingTagsDetailed
                : [
                    ...existingTagsDetailed,
                    {
                      tagId: requestData.tagId,
                      smsId: requestData.id,
                      appliedBy: originalAppliedBy,
                      smsCreatedAt,
                    },
                  ];
              return {
                ...threadItem,
                smsMessage: {
                  ...threadItem.smsMessage,
                  tags: newTagIds,
                  tagsDetailed: newTagsDetailed,
                },
              };
            }
            if (isCallRecordItem(threadItem) && threadItem.callRecord) {
              const existingTags = threadItem.callRecord.tags || [];
              const newTag = {
                tagId: requestData.tagId,
                entityId: threadItem.callRecord.threadCallId,
                entityType: EntityType.ENTITY_TYPE_CALL,
                entityCreatedAt: new Date().toISOString(),
                appliedBy: originalAppliedBy,
              };
              const tagExists = existingTags.some((tag) => tag.tagId === requestData.tagId);
              return {
                ...threadItem,
                callRecord: {
                  ...threadItem.callRecord,
                  tags: tagExists ? existingTags : [...existingTags, newTag],
                },
              };
            }
            return threadItem;
          },
          isOptimistic: false,
          getThreadOnly: true,
        });
      }, delay);
    },
    [delay, dismissTagMutation, unifiedThreadUpdater, onUndoAvailable]
  );
};
